import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API, useUser } from "../contexts/UserContext";
import Header from "../components/Header";
import { Search as SearchIcon, ArrowRight } from "lucide-react";

const highlight = (text, q) => {
 if (!text || !q) return text;
 const idx = text.toLowerCase().indexOf(q.toLowerCase());
 if (idx === -1) return text;
 return (
 <>
 {text.slice(0, idx)}
 <mark className="bg-orange-200 text-zinc-900 px-0.5 rounded-sm">
 {text.slice(idx, idx + q.length)}
 </mark>
 {text.slice(idx + q.length)}
 </>
 );
};

const Search = () => {
 const [params] = useSearchParams();
 const q = params.get("q") || "";
 const [results, setResults] = useState([]);
 const [loading, setLoading] = useState(false);
 const { user } = useUser();

 useEffect(() => {
 if (!q.trim()) { setResults([]); return; }
 setLoading(true);
 axios.get(`${API}/search`, { params: { q } })
 .then((r) => setResults(r.data.results || []))
 .finally(() => setLoading(false));
 }, [q]);

 if (!user) return null;

 return (
 <div className="min-h-screen bg-white ">
 <Header />
 <main className="max-w-5xl mx-auto px-4 md:px-8 py-10">
 <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-600 mb-2">
 Search results
 </div>
 <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-zinc-900 mb-2">
 “{q}”
 </h1>
 <p className="text-sm text-zinc-500 mb-8">
 {loading ? "Searching…" : `${results.length} match${results.length === 1 ? "" : "es"} found`}
 </p>

 {!loading && results.length === 0 && (
 <div
 data-testid="search-no-results"
 className="p-10 border border-dashed border-zinc-300 rounded-sm text-center"
 >
 <SearchIcon className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
 <div className="font-semibold text-zinc-900 mb-1">
 No matches for that query
 </div>
 <div className="text-sm text-zinc-600 ">
 Try a different keyword, or browse modules from the home page.
 </div>
 </div>
 )}

 <div className="space-y-3">
 {results.map((r, i) => (
 <Link
 key={`${r.topic_id}-${i}`}
 to={`/module/${r.module_id}`}
 data-testid={`search-result-${i}`}
 className="group block p-5 border border-zinc-200 rounded-sm bg-white hover:border-orange-500 transition-colors"
 >
 <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-1">
 {r.module_title}
 </div>
 <div className="font-heading text-lg font-bold text-zinc-900 mb-1 group-hover:text-orange-600 :text-orange-400 transition-colors flex items-center gap-2">
 {highlight(r.topic_title, q)}
 <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
 </div>
 {r.snippet && (
 <div className="text-sm text-zinc-600 leading-relaxed">
 {highlight(r.snippet, q)}
 </div>
 )}
 </Link>
 ))}
 </div>
 </main>
 </div>
 );
};

export default Search;
